import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Cookie } from 'lucide-react';
import { Botao } from '../componentes/ui';
import { Cabecalho } from './publico/Cabecalho';
import { Rodape } from './publico/Rodape';
import base from './publico/publico.module.css';
import css from './Privacidade.module.css';
import {
  esquecerConsentimento,
  gravarConsentimento,
  lerConsentimento,
  type Consentimento,
} from '../util/consentimento';

const EMAIL = 'contato@baixaxml.com.br';
const NOME = 'BAIXA XML';
const CNPJ = '43.011.795/0001-46';

/**
 * Seções da política, na ordem. Alimenta o sumário lateral e as âncoras.
 * `certificado-digital` e `cookies` são linkadas de fora (termo do certificado,
 * rodapé) — não renomear.
 */
const SECOES = [
  { id: 'responsavel', titulo: '1. Quem é o responsável' },
  { id: 'papeis', titulo: '2. Dois papéis diferentes, e por que isso importa' },
  { id: 'dados-coletados', titulo: '3. Quais dados coletamos' },
  { id: 'certificado-digital', titulo: '4. Certificado digital e captura automática' },
  { id: 'finalidades', titulo: '5. Por que tratamos cada dado' },
  { id: 'compartilhamento', titulo: '6. Com quem compartilhamos' },
  { id: 'cookies-e-tecnologias', titulo: '7. Cookies e tecnologias semelhantes' },
  { id: 'retencao', titulo: '8. Por quanto tempo guardamos' },
  { id: 'direitos', titulo: '9. Seus direitos' },
  { id: 'seguranca', titulo: '10. Segurança' },
  { id: 'mudancas', titulo: '11. Mudanças nesta política' },
  { id: 'cookies', titulo: '12. Suas preferências de cookies' },
] as const;

/** `id` e título de uma seção — a mesma fonte para o sumário e para a página. */
function secao(id: (typeof SECOES)[number]['id']) {
  const s = SECOES.find((x) => x.id === id)!;
  return { id: s.id, titulo: s.titulo };
}

/**
 * Política de Privacidade.
 *
 * Escrita a partir do que o sistema FAZ, não de um modelo. Cada dado listado
 * aqui é coletado por algum código deste repositório, e cada terceiro citado
 * recebe requisição de verdade. Política genérica é pior que nenhuma: promete
 * o que não se cumpre e esconde o que se faz.
 *
 * O ponto que quase toda política de SaaS fiscal erra, e que está na seção 2:
 * somos **controlador** dos dados de cadastro e **operador** dos dados fiscais
 * que o cliente envia. Um escritório que sobe o SPED de um cliente dele está
 * nos passando dados de terceiros sobre os quais QUEM DECIDE é o escritório,
 * não nós. Tratar tudo como se fosse nosso apagaria essa fronteira — e é ela
 * que define de quem é cada dever.
 */
export function Privacidade() {
  // Quem chega direto em `/privacidade` já recebe o `canonical` e o título
  // corretos no HTML: o build gera um `privacidade.html` próprio (ver
  // `prerenderizar.mjs`). Isto aqui cobre o outro caminho — chegar navegando
  // pela SPA, quando o documento carregado ainda é o da home e as duas tags
  // continuariam apontando para `/`.
  useEffect(() => {
    const tag = document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    const anterior = tag?.href;
    if (tag) tag.href = 'https://baixaxml.com.br/privacidade';
    document.title = 'Política de Privacidade | BAIXA XML';
    return () => {
      if (tag && anterior) tag.href = anterior;
    };
  }, []);

  return (
    <Documento
      titulo="Política de Privacidade"
      atualizado="9 de outubro de 2026"
    >
      <P>
        Esta política explica quais dados pessoais o <b>{NOME}</b> coleta, por
        que coleta, com quem compartilha e o que você pode exigir a respeito
        deles. Foi escrita para ser lida — sem remissões a artigos que você
        teria de procurar.
      </P>

      <Secao {...secao('responsavel')}>
        <P>
          O serviço é operado por <b>{NOME}</b>, inscrita no CNPJ{' '}
          <b>{CNPJ}</b>.
        </P>
        <P>
          <b>Atendimento:</b> todo o atendimento a clientes, inclusive pedidos
          sobre dados pessoais, é feito pelo <b>chat de suporte</b> dentro do
          sistema — fica registrado na sua conta e é respondido por quem cuida
          do serviço. Quem não tem conta (por exemplo, o titular de um dado que
          aparece numa nota fiscal) pode exercer os direitos da seção 9 pelo
          e-mail <A href={`mailto:${EMAIL}`}>{EMAIL}</A>, o canal do
          encarregado pelo tratamento de dados.
        </P>
        <P>
          Somos um agente de tratamento de pequeno porte. As solicitações são
          respondidas dentro dos prazos legais.
        </P>
      </Secao>

      <Secao {...secao('papeis')}>
        <P>
          <b>Sobre os dados da sua conta, somos o controlador.</b> Nome, CPF ou
          CNPJ, e-mail, endereço e histórico de uso existem porque decidimos
          coletá-los para prestar o serviço. As decisões sobre eles são nossas,
          e as obrigações também.
        </P>
        <P>
          <b>Sobre os dados fiscais que você envia, somos apenas o operador.</b>{' '}
          Quando um escritório envia o SPED de um cliente, aquele arquivo contém
          dados de terceiros — fornecedores, destinatários, CNPJs, valores. Quem
          decidiu coletá-los e para que usá-los foi o escritório, não nós. Nós
          os processamos <i>sob instrução</i>: extraímos as chaves, buscamos os
          XMLs e devolvemos. Não usamos esse conteúdo para nada além disso, não
          o vendemos, não o cruzamos com outros clientes e não o analisamos para
          fins próprios.
        </P>
        <P>
          O mesmo vale para as <b>notas capturadas com o certificado digital</b>{' '}
          (seção 4): elas trazem dados de fornecedores e de terceiros, e quem
          decide buscá-las e para que usá-las é o cliente que cadastrou a
          empresa.
        </P>
        <P>
          Na prática: se um titular quiser exercer direitos sobre dados que
          aparecem num SPED ou numa nota fiscal enviada ou capturada por um
          escritório, o pedido deve ser dirigido ao escritório, que é quem
          controla aquele tratamento. Se ele chegar até nós, encaminhamos e
          auxiliamos no que couber.
        </P>
      </Secao>

      <Secao {...secao('dados-coletados')}>
        <Lista>
          <li>
            <b>Cadastro:</b> nome ou razão social, CPF ou CNPJ, e-mail, telefone
            (opcional) e endereço completo. Pedimos o documento porque a emissão
            de cobrança exige identificação do pagador.
          </li>
          <li>
            <b>Senha:</b> guardada apenas como <i>hash</i> (scrypt). Não temos
            como ler sua senha, nem para ajudar — só redefiní-la.
          </li>
          <li>
            <b>Registros de acesso:</b> endereço IP, identificação do navegador,
            data e hora de cada tentativa de login, bem-sucedida ou não. A
            guarda desses registros é exigida pelo Marco Civil da Internet.
          </li>
          <li>
            <b>Uso do serviço:</b> buscas realizadas, arquivos enviados,
            lançamentos de crédito e chaves de API emitidas.
          </li>
          <li>
            <b>Arquivos que você envia:</b> SPED e listas de chaves. Contêm
            dados fiscais de terceiros — ver a seção 2.
          </li>
          <li>
            <b>Certificado digital A1 (e-CNPJ)</b> e a senha dele, quando você
            ativa a captura automática — guardados criptografados. Do
            certificado lemos o CNPJ, o nome do titular, a certificadora e a
            validade. Detalhes na seção 4.
          </li>
          <li>
            <b>Notas fiscais capturadas:</b> as NF-e emitidas contra os CNPJs
            que você cadastrou, com os dados que constam nelas (emitente,
            valores, itens). Dados de terceiros — ver a seção 2.
          </li>
          <li>
            <b>Pagamentos:</b> não recebemos nem armazenamos dados de cartão. A
            cobrança por Pix é emitida pelo Asaas, que recebe seu nome,
            documento e e-mail para gerá-la.
          </li>
          <li>
            <b>Audiência do site:</b> se você consentir, registramos a visita à
            página pública de forma anônima — ver a seção 7.
          </li>
        </Lista>
      </Secao>

      <Secao {...secao('certificado-digital')}>
        <P>
          Para buscar na SEFAZ as notas emitidas contra uma empresa, você nos
          confia o <b>certificado digital A1 (e-CNPJ)</b> dela. Ele é uma
          credencial forte — identifica a empresa perante o Fisco —, por isso
          as regras aqui são estritas e valem como compromisso nosso:
        </P>
        <Lista>
          <li>
            <b>Uso exclusivo.</b> O certificado é usado <i>apenas</i> para (1)
            se identificar no serviço de Distribuição de Documentos Fiscais da
            SEFAZ (Ambiente Nacional) e consultar as NF-e destinadas ao CNPJ, e
            (2) assinar o evento de <b>Ciência da Operação</b> dessas notas,
            quando a opção estiver ligada. Nunca o usamos para emitir notas,
            cancelar documentos, acessar o e-CAC, assinar contratos ou qualquer
            outra finalidade.
          </li>
          <li>
            <b>O que a Ciência significa.</b> É um registro, junto à SEFAZ, de
            que a empresa tomou conhecimento da nota — a etapa que libera o XML
            completo. Não confirma nem recusa a operação. A Confirmação, o
            Desconhecimento e a Operação não Realizada continuam sendo decisão
            sua, feitas pelo seu sistema; nós não as enviamos.
          </li>
          <li>
            <b>Autorização expressa.</b> O envio do certificado só é aceito com
            o aceite do termo de autorização, que registramos com data, hora e
            endereço IP. Você declara ter poderes para autorizar esse uso em
            nome da empresa.
          </li>
          <li>
            <b>Guarda criptografada.</b> O arquivo e a senha são cifrados
            (AES-256-GCM) antes de chegar ao banco de dados, com uma chave
            mantida fora dele. O certificado só é aberto na memória do servidor,
            no momento da consulta, e nunca é gravado em disco sem
            criptografia.
          </li>
          <li>
            <b>Ninguém baixa o certificado.</b> Não existe tela nem função que
            devolva o arquivo — nem para você, nem para a nossa equipe.
          </li>
          <li>
            <b>Conexão direta com a SEFAZ.</b> As consultas vão do nosso
            servidor direto aos webservices oficiais, por conexão cifrada e
            autenticada pelo próprio certificado. Nenhum intermediário recebe o
            certificado ou as notas.
          </li>
          <li>
            <b>Revogação a qualquer momento.</b> Apagar o certificado no painel
            elimina o arquivo e a senha do banco na hora e interrompe a captura.
            Certificados vencidos há mais de 30 dias são apagados
            automaticamente.
          </li>
        </Lista>
        <P>
          <b>Base legal:</b> execução do contrato (art. 7º, V, da LGPD), com a
          autorização expressa do termo. Os XMLs capturados são os documentos
          originais emitidos pelos fornecedores, com a assinatura digital
          deles, e são mostrados apenas à conta que cadastrou a empresa.
        </P>
      </Secao>

      <Secao {...secao('finalidades')}>
        <Lista>
          <li>
            <b>Executar o contrato:</b> criar e manter sua conta, processar as
            buscas, capturar as notas das empresas cadastradas, entregar os
            XMLs, controlar saldo e cobrar pelo uso.
          </li>
          <li>
            <b>Cumprir obrigação legal:</b> guardar registros de acesso e emitir
            documentos fiscais das cobranças.
          </li>
          <li>
            <b>Legítimo interesse:</b> segurança (detectar tentativas de invasão
            e uso indevido) e medição anônima de audiência do site.
          </li>
          <li>
            <b>Consentimento:</b> apenas para a medição de audiência, que você
            pode ligar e desligar a qualquer momento no fim desta página.
          </li>
        </Lista>
        <P>
          Não usamos seus dados para publicidade, não os vendemos e não os
          cedemos a corretores de dados. Não há decisão automatizada que produza
          efeitos jurídicos sobre você.
        </P>
      </Secao>

      <Secao {...secao('compartilhamento')}>
        <P>
          Só com quem é necessário para o serviço funcionar, e só o mínimo que
          cada um precisa:
        </P>
        <Lista>
          <li>
            <b>Asaas</b> (instituição de pagamento, Brasil) — nome, documento e
            e-mail, para emitir a cobrança Pix.
          </li>
          <li>
            <b>Provedor de e-mail transacional</b> — seu endereço de e-mail e o
            conteúdo das mensagens que enviamos (código de verificação, avisos
            da conta).
          </li>
          <li>
            <b>Provedor de infraestrutura</b> — hospeda a aplicação e o banco de
            dados.
          </li>
        </Lista>
        <P>
          Para <b>obter os XMLs</b>, consultamos serviços públicos de consulta
          de documentos fiscais usando a chave de acesso que você informou.
          Enviamos a chave — não enviamos seus dados de cadastro.
        </P>
        <P>
          Na <b>captura automática</b>, falamos com a <b>SEFAZ</b> (Ambiente
          Nacional da NF-e), que recebe o CNPJ consultado, a identificação do
          certificado (a parte pública dele, como em qualquer conexão
          autenticada) e os eventos de Ciência assinados. A chave privada do
          certificado nunca sai do nosso servidor.
        </P>
        <P>
          <b>Transferência internacional:</b> alguns desses fornecedores mantêm
          servidores fora do Brasil. Quando isso ocorre, a transferência se
          apoia nas hipóteses do art. 33 da LGPD e se limita ao necessário para
          o serviço.
        </P>
        <P>
          Também podemos compartilhar dados por ordem judicial ou requisição de
          autoridade competente. Nesses casos, informamos o titular sempre que a
          lei permitir.
        </P>
      </Secao>

      <Secao {...secao('cookies-e-tecnologias')}>
        <P>
          Este site{' '}
          <b>
            não usa cookies de publicidade, de redes sociais ou de rastreamento
            entre sites
          </b>
          . Nenhum terceiro recebe dados de navegação. O que existe é:
        </P>
        <Tabela
          linhas={[
            [
              'bx_sessao / bx_refresh',
              'Necessário',
              'Mantêm você conectado e renovam a sessão sem pedir a senha de novo. Não são legíveis por scripts da página.',
            ],
            [
              'bx_csrf',
              'Necessário',
              'Impede que outro site execute ações na sua conta aproveitando a sessão aberta.',
            ],
            [
              'bx_consent',
              'Necessário',
              'Guarda a escolha que você fez aqui, para não perguntarmos de novo.',
            ],
            [
              'bx_visita',
              'Audiência',
              'Evita contar a mesma aba duas vezes. Vale só enquanto a aba está aberta.',
            ],
            [
              'bx_origem',
              'Audiência',
              'Lembra de qual site você chegou da primeira vez. Só é enviado se você criar uma conta, para sabermos quais canais trazem clientes; recusar a medição apaga.',
            ],
          ]}
        />
        <P>
          A <b>medição de audiência</b> registra que uma visita aconteceu, em
          qual página, de qual site você veio, o tipo de aparelho (celular,
          tablet ou computador) e em quais botões da página você clicou. Para
          saber se duas visitas são da mesma pessoa <i>no mesmo dia</i>, o
          servidor guarda um código derivado do seu IP e do seu navegador —{' '}
          <b>nunca o IP em si</b> — e a chave que gera esse código muda todos os
          dias. Passada a virada do dia, não há como ligar as visitas de ontem
          às de hoje, nem por nós, nem por quem obtivesse o banco de dados.
          Esses registros são apagados após 180 dias.
        </P>
      </Secao>

      <Secao {...secao('retencao')}>
        <Lista>
          <li>
            <b>Dados de cadastro:</b> enquanto a conta existir e, depois do
            encerramento, pelo prazo necessário para cumprir obrigações legais e
            fiscais.
          </li>
          <li>
            <b>Registros de acesso:</b> pelo prazo do Marco Civil da Internet.
          </li>
          <li>
            <b>Arquivos SPED e XMLs baixados:</b> as buscas e os arquivos são
            removidos automaticamente 48 horas após a conclusão. Baixe o ZIP
            antes disso — depois, não temos como recuperá-lo.
          </li>
          <li>
            <b>Notas da captura automática:</b> ficam disponíveis enquanto a
            empresa estiver na sua carteira, para você baixar quando precisar.
            Remover a empresa apaga as notas e os XMLs dela. A guarda fiscal de
            5 anos é obrigação do contribuinte — baixe e arquive os XMLs no seu
            sistema.
          </li>
          <li>
            <b>Certificado digital:</b> até você apagá-lo, ou 30 dias depois do
            vencimento, o que vier primeiro.
          </li>
          <li>
            <b>Lançamentos de crédito:</b> mantidos como histórico financeiro,
            porque são a prova do que foi cobrado.
          </li>
          <li>
            <b>Medição de audiência:</b> 180 dias.
          </li>
        </Lista>
      </Secao>

      <Secao {...secao('direitos')}>
        <P>
          A LGPD garante a você, sobre os seus dados pessoais, o direito de:
        </P>
        <Lista>
          <li>
            <b>Confirmar</b> se tratamos dados seus e <b>acessá-los</b>.
          </li>
          <li>
            <b>Corrigir</b> dados incompletos, inexatos ou desatualizados — o
            que você também pode fazer sozinho, em Configurações.
          </li>
          <li>
            <b>Solicitar anonimização, bloqueio ou eliminação</b> de dados
            desnecessários, excessivos ou tratados fora da lei.
          </li>
          <li>
            <b>Pedir a portabilidade</b> a outro fornecedor.
          </li>
          <li>
            <b>Saber com quem compartilhamos</b> seus dados.
          </li>
          <li>
            <b>Revogar o consentimento</b> e ser informado das consequências da
            negativa.
          </li>
          <li>
            <b>Opor-se</b> a tratamento feito com base em legítimo interesse.
          </li>
        </Lista>
        <P>
          Clientes fazem o pedido pelo chat de suporte do sistema; quem não tem
          conta, pelo e-mail <A href={`mailto:${EMAIL}`}>{EMAIL}</A>. Podemos pedir
          informações que confirmem sua identidade — não para dificultar, mas
          porque entregar dados à pessoa errada seria o próprio vazamento que
          esses direitos existem para evitar.
        </P>
        <P>
          Alguns dados não podem ser apagados a pedido: registros de acesso e
          documentos fiscais têm guarda obrigatória por lei. Nesses casos,
          explicamos o motivo e o prazo.
        </P>
      </Secao>

      <Secao {...secao('seguranca')}>
        <P>
          As senhas são guardadas apenas como <i>hash</i>. Certificados
          digitais e suas senhas, cifrados com AES-256-GCM e chave mantida fora
          do banco de dados. A sessão fica em
          cookie que o JavaScript da página não consegue ler, com proteção
          contra requisições forjadas por outros sites. Todo o tráfego é cifrado
          por HTTPS. Cada conta enxerga apenas os próprios dados, e as ações
          administrativas ficam registradas com autor, alvo e origem.
        </P>
        <P>
          Nenhum sistema é imune. Se ocorrer um incidente de segurança com risco
          relevante aos titulares, comunicaremos os afetados e a Autoridade
          Nacional de Proteção de Dados, como manda o art. 48 da LGPD.
        </P>
      </Secao>

      <Secao {...secao('mudancas')}>
        <P>
          Se ela mudar de forma relevante, avisaremos pelo site ou por e-mail
          antes de a mudança valer. A data de atualização fica sempre no topo
          desta página.
        </P>
      </Secao>

      <Secao {...secao('cookies')}>
        <PainelPreferencias />
      </Secao>
    </Documento>
  );
}

/**
 * Controle de consentimento dentro da própria política.
 *
 * A LGPD exige que revogar seja tão fácil quanto consentir. Um banner que
 * aparece uma vez e some cumpre a primeira metade e ignora a segunda — por isso
 * o controle vive aqui, num endereço fixo, linkado do rodapé de toda página.
 */
function PainelPreferencias() {
  const [atual, setAtual] = useState<Consentimento | null>(null);

  useEffect(() => {
    setAtual(lerConsentimento());
    const aoMudar = () => setAtual(lerConsentimento());
    window.addEventListener('bx:consentimento', aoMudar);
    return () => window.removeEventListener('bx:consentimento', aoMudar);
  }, []);

  return (
    <div className={css.painel}>
      <span className={css.painelEstado}>
        <Cookie aria-hidden="true" />
        <span>
          {atual === null ? (
            <>Você ainda não registrou uma escolha neste navegador.</>
          ) : (
            <>
              Sua escolha atual:{' '}
              <b className={atual.audiencia ? css.permitido : css.restrito}>
                {atual.audiencia
                  ? 'medição de audiência permitida'
                  : 'apenas os cookies necessários'}
              </b>
              , registrada em{' '}
              {new Date(atual.decididoEm).toLocaleString('pt-BR')}.
            </>
          )}
        </span>
      </span>

      <div className={css.painelAcoes}>
        <Botao pequeno onClick={() => gravarConsentimento(true)}>
          Permitir medição de audiência
        </Botao>
        <Botao
          aparencia="contorno"
          pequeno
          onClick={() => gravarConsentimento(false)}
        >
          Apenas os necessários
        </Botao>
        <Botao
          aparencia="contorno"
          pequeno
          onClick={() => esquecerConsentimento()}
        >
          Apagar minha escolha
        </Botao>
      </div>

      <span className={css.painelNota}>
        A escolha fica guardada apenas neste navegador. Se você usar outro
        aparelho ou limpar os dados do site, perguntaremos de novo.
      </span>
    </div>
  );
}

/* ── Moldura e peças de texto ────────────────────────────────────────────── */

function Documento({
  titulo,
  atualizado,
  children,
}: {
  titulo: string;
  atualizado: string;
  children: ReactNode;
}) {
  return (
    <div className={base.pagina}>
      <Cabecalho />

      <header className={css.abertura}>
        <div className={base.conteudo}>
          <nav aria-label="Você está em" className={css.migalhas}>
            <Link to="/">Início</Link>
            <ChevronRight aria-hidden="true" />
            <span aria-current="page">{titulo}</span>
          </nav>
          <h1 className={css.titulo}>{titulo}</h1>
          <span className={css.atualizado}>
            Última atualização: {atualizado}
          </span>
        </div>
      </header>

      <div className={`${base.conteudo} ${css.grade}`}>
        <aside className={css.lateral}>
          <nav aria-label="Sumário" className={css.sumario}>
            <span className={css.sumarioTitulo}>Nesta política</span>
            {SECOES.map((s) => (
              <a key={s.id} href={`#${s.id}`}>
                {s.titulo}
              </a>
            ))}
          </nav>
        </aside>
        <main className={css.documento}>{children}</main>
      </div>

      <Rodape />
    </div>
  );
}

function Secao({
  titulo,
  id,
  children,
}: {
  titulo: string;
  /** Âncora para links diretos (o termo do certificado aponta para cá). */
  id?: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className={css.secao}>
      <h2>{titulo}</h2>
      {children}
    </section>
  );
}

function P({ children }: { children: ReactNode }) {
  return <p className={css.p}>{children}</p>;
}

function Lista({ children }: { children: ReactNode }) {
  return <ul className={css.lista}>{children}</ul>;
}

function A({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} className={css.a}>
      {children}
    </a>
  );
}

function Tabela({ linhas }: { linhas: Array<[string, string, string]> }) {
  return (
    <div className={css.tabelaMoldura}>
      <table className={css.tabela}>
        <thead>
          <tr>
            {['Nome', 'Categoria', 'Para que serve'].map((t) => (
              <th key={t}>{t}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map(([nome, categoria, uso]) => (
            <tr key={nome}>
              <td>
                <code>{nome}</code>
              </td>
              <td className={css.categoria}>{categoria}</td>
              <td>{uso}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
